import React, { useEffect } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { X } from 'lucide-react';
export type LegalDoc = 'privacy' | 'terms';
interface LegalModalProps {
  doc: LegalDoc | null;
  onClose: () => void;
}
const updated = 'June 15, 2026';
const content: Record<LegalDoc, {
  title: string;
  sections: {
    heading: string;
    body: string[];
  }[];
}> = {
  privacy: {
    title: 'Privacy Policy',
    sections: [{
      heading: '1. Overview',
      body: ['This Privacy Policy explains how Alex Circei collects, uses, and protects information when you visit this site or engage advisory services. The approach is deliberately minimal: only what is necessary to respond to you and deliver an engagement is collected.']
    }, {
      heading: '2. Information collected',
      body: ['Contact details you provide voluntarily, such as your name, email address, organization, and any context you share when booking a session or emailing directly.', 'Basic, aggregated analytics about site usage. This is never used to identify you individually.']
    }, {
      heading: '3. How it is used',
      body: ['To respond to enquiries, schedule and conduct working sessions, and deliver agreed engagements.', 'To improve the content and structure of this site.', 'Personal information is never sold, rented, or traded to third parties.']
    }, {
      heading: '4. Third-party services',
      body: ['Enquiries are submitted through the Waydev contact desk and processed under Waydev’s privacy policy. No engagement content is shared with advertisers.']
    }, {
      heading: '5. Engagement confidentiality',
      body: ['Data reviewed during an advisory engagement, including engineering telemetry and repository metadata, is handled under the terms of the relevant engagement agreement or mutual NDA and is not retained beyond its stated purpose.']
    }, {
      heading: '6. Retention and your rights',
      body: ['Correspondence is retained only as long as needed for the working relationship or as required by law. You may request access to, correction of, or deletion of your information at any time via the contact desk at waydev.co/contact.', 'Where GDPR or CCPA applies, you additionally hold the right to object to processing, to request portability of your data, and to lodge a complaint with your local supervisory authority.']
    }, {
      heading: '7. International transfers',
      body: ['Enquiries may be processed in the United States and the European Union. Where personal data is transferred across borders it is covered by Standard Contractual Clauses or an equivalent lawful transfer mechanism.']
    }, {
      heading: '8. Changes to this policy',
      body: ['This policy may be updated as the advisory practice or its tooling changes. The revision date at the top of this document always reflects the current version.']
    }]
  },
  terms: {
    title: 'Terms of Service',
    sections: [{
      heading: '1. Acceptance',
      body: ['By accessing this site or engaging advisory services, you agree to these Terms of Service. If you do not agree, please do not use the site or services.']
    }, {
      heading: '2. Nature of services',
      body: ['Advisory services cover engineering operating models, AI ROI measurement, SDLC and metrics program design, tooling strategy, and technical due diligence. Advice is provided on a best-effort, good-faith basis and is informational in nature.', 'No specific outcome is guaranteed. Results depend on factors outside the advisor’s control, including organizational adoption and data quality.']
    }, {
      heading: '3. Engagements and fees',
      body: ['Scope, deliverables, timelines, and fees for paid engagements are agreed separately in writing before work begins. An introductory conversation carries no obligation.']
    }, {
      heading: '4. Confidentiality',
      body: ['Information shared during engagements is treated as confidential and is not disclosed to third parties without consent, except where required by law. A mutual NDA can be put in place on request.']
    }, {
      heading: '5. Independence from Waydev',
      body: ['Advisory engagements are conducted independently. Where Waydev is a suitable fit for a stated problem it will be identified as such; where it is not, that will be stated plainly. Engaging the advisory service creates no obligation to license the platform.']
    }, {
      heading: '6. Intellectual property',
      body: ['Frameworks, templates, and materials provided during an engagement remain the intellectual property of Alex Circei and are licensed to you for internal use within your organization.']
    }, {
      heading: '7. Limitation of liability',
      body: ['To the maximum extent permitted by law, liability arising from any engagement is limited to the fees paid for that engagement. No liability is accepted for indirect or consequential damages.']
    }, {
      heading: '8. Non-solicitation and independence',
      body: ['Advisory work does not create an employment, partnership, or joint venture relationship. Engagements are non-exclusive, and comparable work may be undertaken for other organizations, always subject to the confidentiality obligations above.']
    }, {
      heading: '9. Governing law',
      body: ['These terms are governed by the laws of the State of Delaware, United States, without regard to conflict of law principles. Any dispute will be brought before the courts of that jurisdiction.']
    }, {
      heading: '10. Changes to these terms',
      body: ['These terms may be revised from time to time. Continued use of the site or continuation of an engagement after a revision constitutes acceptance of the updated terms.']
    }]
  }
};
export function LegalModal({
  doc,
  onClose
}: LegalModalProps) {
  const reduce = useReducedMotion();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (doc) {
      document.addEventListener('keydown', onKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [doc, onClose]);
  const data = doc ? content[doc] : null;
  return <AnimatePresence data-id="element-87" data-mp="19bdut2k2iijr">
      {doc && data && <motion.div className="fixed inset-0 z-[100] flex items-stretch justify-center p-0 sm:items-center sm:p-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.2, 0, 0, 1]
    }} data-id="element-88" data-mp="3kwcyn1d3etwi">
          <div className="absolute inset-0 bg-ink/60" onClick={onClose} aria-hidden="true" data-id="element-89" data-mp="3g54n9zs7cygp" />

          <motion.div role="dialog" aria-modal="true" aria-label={data.title} initial={reduce ? false : {
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 12
      }} transition={{
        duration: 0.25,
        ease: [0.2, 0, 0, 1]
      }} className="relative flex h-full w-full max-w-2xl flex-col bg-bone sm:h-auto sm:max-h-[85vh] sm:border sm:border-ink" data-id="element-90" data-mp="324tyr3ed7vyp">
            <div className="flex items-center justify-between border-b border-rule px-6 py-3.5" data-id="element-91" data-mp="vew2l4f4ieqc">
              <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-ink-45" data-id="element-92" data-mp="3fawuao1zeyzp">
                {data.title}
              </p>
              <button onClick={onClose} aria-label="Close" className="text-ink-45 transition-colors duration-200 ease-editorial hover:text-signal" data-id="element-93" data-mp="35ln278751gbl">
                <X className="h-4 w-4" data-id="element-94" data-mp="2vo8mxbwyaxkv" />
              </button>
            </div>

            <div className="overflow-y-auto px-6 py-8 md:px-9" data-id="element-95" data-mp="2rm58qfiwdevw">
              <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-signal" data-id="element-96" data-mp="2rku3scdpu137">
                Last updated {updated}
              </p>
              <h2 className="mt-2 font-serif text-[clamp(26px,3.4vw,36px)] font-medium leading-[1.08] tracking-[-0.02em]" data-id="element-97" data-mp="2hohbnixtjvak">
                {data.title}
              </h2>

              <div className="mt-8" data-id="element-98" data-mp="ktbg6wnghh17">
                {data.sections.map(section => <div key={section.heading} className="border-t border-rule-soft py-5" data-id="element-99" data-mp="fuhzywgsbbxc">
                    <h3 className="font-mono text-[12px] uppercase tracking-[0.1em] text-ink" data-id="element-100" data-mp="sef0dbdirrgm">
                      {section.heading}
                    </h3>
                    <div className="mt-3 space-y-3" data-id="element-101" data-mp="x89jodp7q4f6">
                      {section.body.map((paragraph, i) => <p key={i} className="text-[14.5px] leading-relaxed text-ink-70" data-id="element-102" data-mp="4hhqzq7cvxwl" data-mpi={i}>
                          {paragraph}
                        </p>)}
                    </div>
                  </div>)}
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}